import { useEffect, useState } from "react";
import "../../../pages/navbar.css";
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Divider,
} from "@mui/material";
import "./EditProfile.css";
import { UpdateProfile } from "../../../queries/updateProfile";
import { clientConfig } from "../../../awsClientConfig";
import { userProfileData } from "../../../Common/profileInfo";
import { formatPhone, formatZipcode } from "../../../functions/misc";
import { GetProfilePrimaryAddress } from "../../../queries/GetProfilePrimaryAddress";
import { UpsertProfilePrimaryAddress } from "../../../queries/UpsertProfilePrimaryAddress";
import Loading from "../../../Common/Loading";
import { US_STATES } from "../../../Common/States";
import { useLocation, useNavigate } from "react-router-dom";
import useProfileStore from "../../../store/ProfileStore";
import Footer from "../../footer";
import AppBreadcrumbs from "../../AppBreadCrumbs";
interface info {
  firstName: string;
  lastName: string;
  middleName: string;
  phone: string;
  email: string;
}

function EditProfile() {
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState({
    zipCode: "",
    languages: "",
    phone: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");
  const [address, setAddress] = useState({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [isFormChanged, setisFormChanged] = useState(false);
  const [personInfo, setPersonInfo] = useState({});
  const [formValue, setFormValue] = useState<info>({
    firstName: "",
    lastName: "",
    middleName: "",
    phone: "",
    email: "",
  });
  const [languages, setLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { primaryProfile, setPrimaryProfile } = useProfileStore();
  const client = new clientConfig();
  useLocation();
  useEffect(() => {
    getProfile();
    getProfilePrimaryAddress();
  }, [primaryProfile]);

  const getProfile = async () => {
    if (primaryProfile?.FirstName) {
      setPersonInfo(primaryProfile);
      setFormValue({
        firstName: primaryProfile.FirstName ?? "",
        middleName: primaryProfile.MiddleName ?? "",
        lastName: primaryProfile.LastName ?? "",
        phone: formatPhone(primaryProfile.Phone),
        email: primaryProfile.Email ?? "",
      });
      setLanguages(primaryProfile?.Languages ?? []);
      setLoading(false);
    }
  };

  const getProfilePrimaryAddress = async () => {
    const profileId = await userProfileData();
    client
      .query({
        query: GetProfilePrimaryAddress,
        variables: { profileId: profileId },
      })
      .then((res: any) => {
        if (res.data.GetProfilePrimaryAddress !== null) {
          setAddress({
            streetAddress1:
              res.data.GetProfilePrimaryAddress?.StreetAddress1 ?? "",
            streetAddress2:
              res.data.GetProfilePrimaryAddress?.StreetAddress2 ?? "",
            city: res.data.GetProfilePrimaryAddress?.City ?? "",
            state: res.data.GetProfilePrimaryAddress?.State ?? "",
            zipCode: res.data.GetProfilePrimaryAddress?.ZipCode ?? "",
          });
          setLoading(false);
        }
      })
      .catch((error: any) => {
        console.log(error, "error");
      });
  };

  const formatPhoneNumber = (e: any) => {
    let _error: any = error;
    _error[e.target.name] = "";

    const str = e.target.value;
    const phoneNumber = str.replace(/[^\d]/g, "");

    if (phoneNumber.length < 4) {
      setFormValue({ ...formValue, [e.target.name]: phoneNumber });
      return;
    }

    if (phoneNumber.length < 7) {
      setFormValue({
        ...formValue,
        [e.target.name]: `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`,
      });
      return;
    }

    setFormValue({
      ...formValue,
      [e.target.name]: `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`,
    });
    return;
  };

  const setAddressData = (e: any) => {
    setisFormChanged(true);
    setAddress((prevState: any) => {
      return {
        ...prevState,
        [e.target.name]:
          e.target.name !== "zipCode" ? e.target.value : formatZipcode(e),
      };
    });
    let _error: any = error;
    _error[e.target.name] = "";
  };
  const setProfile = (e: any) => {
    setFormValue({ ...formValue, [e.target.name]: e.target.value });
  };

  const onChange = (event: any) => {
    let language: any;
    if (languages?.length) language = [...languages];
    else language = [];

    const index = language.indexOf(event.target.value);

    if (event.target.checked) {
      language.push(event.target.value);
    } else if (!event.target.checked) {
      language.splice(index, 1);
    }
    setisFormChanged(true);
    setLanguages(language);
    let _error = error;
    _error["languages"] = "";
    setError(_error);
    setError({ ...error });
  };

  const submitInfo = async () => {
    setSubmitError("");
    const variables = {
      ...personInfo,
      Id: primaryProfile?.Id,
      Active: true,
      FirstName: formValue.firstName,
      MiddleName: formValue.middleName ? formValue.middleName : null,
      LastName: formValue.lastName,
      Phone: formValue.phone.replace(/[^0-9]/g, ""),
      Languages: languages,
    };
    client
      .mutate({ mutation: UpdateProfile, variables: variables })
      .then((res: any) => {
        if (res.data.UpdateProfile) {
          setPrimaryProfile({
            Id: primaryProfile?.Id,
            PartnerId: primaryProfile?.PartnerId,
            Email: primaryProfile?.Email,
            FirstName: formValue.firstName,
            MiddleName: formValue.middleName ? formValue.middleName : null,
            LastName: formValue.lastName,
            Phone: formValue.phone.replace(/[^0-9]/g, ""),
            LegacyClient: primaryProfile?.LegacyClient,
            Languages: languages ? languages : null,
            EnrollmentSource: primaryProfile?.EnrollmentSource
          });
          submitAddress();
        }
      })
      .catch((error: any) => {
        console.log("error:", error);
        setSubmitError(
          "For your security we can’t update your personal info online. Call us at (800) 536-0734 to make any changes."
        );
      });
  };

  const submitAddress = async () => {
    const profileId = await userProfileData();
    const variables = {
      ProfileId: profileId,
      State: address.state,
      City: address.city,
      StreetAddress1: address.streetAddress1,
      ZipCode: address.zipCode,
      StreetAddress2: address.streetAddress2 ? address.streetAddress2 : null,
    };
    client
      .mutate({ mutation: UpsertProfilePrimaryAddress, variables: variables })
      .then((res: any) => {
        if (res.data.UpsertProfilePrimaryAddress) {
          setSuccess("Updated Profile Information");
          setTimeout(() => {
            setSuccess("");
          }, 5000);
        }
      })
      .catch((error: any) => {
        console.log(error);
        setSubmitError(
          "For your security we can’t update your personal info online. Call us at (800) 536-0734 to make any changes."
        );
      });
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (form.checkValidity()) {
      const _error = error;

      if (
        address.zipCode.length < 5 ||
        (address.zipCode.length > 5 && address.zipCode.length < 10) ||
        languages?.length == 0 ||
        formValue.phone.length < 14
      ) {
        if (address.zipCode === "")
          _error["zipCode"] = "Please provide ZIP Code.";
        else if (
          address.zipCode.length < 5 ||
          (address.zipCode.length > 5 && address.zipCode.length < 10)
        )
          _error["zipCode"] = "ZIP Code has invalid number of characters.";
        if (languages?.length == 0)
          _error["languages"] = "Select at least one language.";
        if (formValue.phone === "")
          _error["phone"] = "Please provide Phone Number.";
        else if (formValue.phone.length < 14)
          _error["phone"] = "Phone has invalid number of characters.";
        setError(_error);
        setError({ ...error });
        return;
      } else {
        _error["zipCode"] = "";
        _error["languages"] = "";
        _error["phone"] = "";
        setError(_error);
        setError({ ...error });
      }

      submitInfo();
    }

    setValidated(true);
  };
  return (
    <>
      <Container sx={{ mb: 5 }}>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              width: "80%",
            }}
          >
            <Box className="headerTitleBox">
              <h1 className="headerTitle">
                Profile & Settings
              </h1>
              <AppBreadcrumbs
                items={[
                  { label: 'Home', path: '/dashboard' },
                  {
                    label: 'Account Settings',
                  },
                  { label: 'Edit Profile' },
                ]}
              />
            </Box>

            {loading ? (
              <Loading />
            ) : (
              <Box className="formContainerBox">
                <Box sx={{ py: 4, px: { xs: 2, sm: 4, md: 4, lg: 4 } }}>
                  <form noValidate onSubmit={handleSubmit}>
                    <Grid
                      container
                      spacing={3}
                      sx={{
                        mt: 3,
                        "& .MuiGrid-item": { paddingTop: "12px" },
                        "& .MuiGrid-item:first-of-type": { paddingTop: 0 },
                      }}
                    >
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#212636",
                            fontSize: "18px",
                          }}
                        >
                          Personal Details
                        </Typography>
                        <Typography
                          sx={{
                            color: "#FB5E56",
                            fontSize: "12px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "20px",
                            letterSpacing: "0.4px",
                            paddingTop: "12px",
                          }}
                        >
                          For your security we can’t update your personal info
                          online. Call us at (800) 536-0734 to make any changes.
                        </Typography>
                      </Grid>
                      {submitError.length ? (
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: "#FB5E56",
                              fontSize: "12px",
                              lineHeight: "20px",
                            }}
                          >
                            {submitError}
                          </Typography>
                        </Grid>
                      ) : (
                        <></>
                      )}
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <TextField
                          label="First Name"
                          fullWidth
                          required
                          value={formValue.firstName}
                          name="firstName"
                          onChange={setProfile}
                          InputProps={{
                            readOnly: true,
                            sx: { backgroundColor: "#EDF7FF" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <TextField
                          label="Middle Name"
                          fullWidth
                          value={formValue.middleName}
                          name="middleName"
                          onChange={setProfile}
                          InputProps={{
                            readOnly: true,
                            sx: { backgroundColor: "#EDF7FF" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={4}>
                        <TextField
                          label="Last Name"
                          fullWidth
                          required
                          value={formValue.lastName}
                          name="lastName"
                          onChange={setProfile}
                          InputProps={{
                            readOnly: true,
                            sx: { backgroundColor: "#EDF7FF" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={6}>
                        <TextField
                          label="Email Address"
                          fullWidth
                          required
                          value={formValue.email}
                          name="email"
                          onChange={setProfile}
                          InputProps={{
                            readOnly: true,
                            sx: { backgroundColor: "#EDF7FF" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={6}>
                        <TextField
                          label="Phone"
                          fullWidth
                          value={formValue.phone}
                          name="phone"
                          onChange={(e) => formatPhoneNumber(e)}
                          InputProps={{
                            readOnly: true,
                            sx: { backgroundColor: "#EDF7FF" },
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Divider />
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      sx={{
                        mt: 1,
                        "& .MuiGrid-item": { paddingTop: "12px" },
                        "& .MuiGrid-item:last-of-type": { paddingTop: "24px" },
                      }}
                    >
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: "#212636",
                            fontSize: "18px",
                          }}
                        >
                          Primary Address
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            color: "#212636",
                            fontSize: "14px",
                            lineHeight: "20px",
                            letterSpacing: "0.4000000059604645px",
                          }}
                        >
                          Your primary address is where you live -- typically,
                          what’s on your driver’s license or other
                          government-issued ID.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <TextField
                          label="Street Address"
                          fullWidth
                          required
                          value={address.streetAddress1}
                          placeholder="Street number, etc"
                          name="streetAddress1"
                          onChange={(e) => setAddressData(e)}
                          error={validated && !address.streetAddress1}
                          helperText={
                            validated && !address.streetAddress1
                              ? "Please provide Street Address."
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <TextField
                          label="Street Address 2 (Optional)"
                          fullWidth
                          placeholder="Street number, etc"
                          value={address.streetAddress2}
                          name="streetAddress2"
                          onChange={(e) => setAddressData(e)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={6} lg={6}>
                        <TextField
                          label="City"
                          fullWidth
                          required
                          value={address.city}
                          name="city"
                          onChange={(e) => setAddressData(e)}
                          error={validated && !address.city}
                          helperText={
                            validated && !address.city
                              ? "Please provide City."
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={6} lg={6}>
                        <FormControl
                          fullWidth
                          required
                          error={validated && !address.state}
                        >
                          <InputLabel>State</InputLabel>
                          <Select
                            value={address.state}
                            name="state"
                            onChange={(e) => setAddressData(e)}
                            label="State"
                          >
                            <MenuItem value="">-</MenuItem>
                            {US_STATES.map((state) => (
                              <MenuItem
                                key={state.abbreviation}
                                value={state.abbreviation}
                              >
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {validated && !address.state && (
                            <FormHelperText>
                              Please provide State.
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4} md={12} lg={12}>
                        <TextField
                          label="ZIP Code"
                          fullWidth
                          value={address.zipCode}
                          name="zipCode"
                          onChange={(e) => setAddressData(e)}
                          error={!!error.zipCode}
                          helperText={error.zipCode}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Divider />
                      </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ my: 3 }}>
                      Preferred Languages
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        letterSpacing: "0.4000000059604645px",
                        textAlign: "left",
                      }}
                    >
                      Choose the language you’d like us to use when we talk to
                      you (for example, on the phone or in some of the alerts
                      and notifications we send you)
                    </Typography>
                    <Grid
                      container
                      spacing={4}
                      gap={2}
                      sx={{ padding: "12px" }}
                    >
                      <Grid item xs={4} sm={3} md={2} lg={2}>
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="English"
                              checked={languages.includes("English")}
                              onChange={onChange}
                              value="English"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="English"
                        />
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Hindi"
                              checked={languages.includes("Hindi")}
                              onChange={onChange}
                              value="Hindi"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="Hindi"
                        />
                      </Grid>
                      <Grid item xs={4} sm={3} md={2} lg={2}>
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Spanish"
                              checked={languages.includes("Spanish")}
                              onChange={onChange}
                              value="Spanish"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="Spanish"
                        />
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="French"
                              checked={languages.includes("French")}
                              onChange={onChange}
                              value="French"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="French"
                        />
                      </Grid>
                      <Grid item xs={4} sm={3} md={2} lg={2}>
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Korean"
                              checked={languages.includes("Korean")}
                              onChange={onChange}
                              value="Korean"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="Korean"
                        />
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Vietnamese"
                              checked={languages.includes("Vietnamese")}
                              onChange={onChange}
                              value="Vietnamese"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="Vietnamese"
                        />
                      </Grid>
                      <Grid item xs={4} sm={3} md={4} lg={4}>
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Chinese"
                              checked={languages.includes("Chinese")}
                              onChange={onChange}
                              value="Chinese"
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                            />
                          }
                          label="Chinese (Mandarin)"
                        />
                        <FormControlLabel
                          sx={{ padding: "16px", gap: "4px" }}
                          control={
                            <Checkbox
                              id="Other"
                              checked={languages.includes("Other")}
                              onChange={onChange}
                              sx={{ "&.Mui-checked": { color: "#1D89D2" } }}
                              value="Other"
                            />
                          }
                          label="Other"
                        />
                      </Grid>
                    </Grid>
                    {error.languages && (
                      <Typography
                        color="error"
                        sx={{ mt: 2, ml: 2, fontSize: "16px" }}
                      >
                        {error.languages}
                      </Typography>
                    )}
                    <Divider />
                    <Grid
                      container
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 3 }}
                    >
                      {success && (
                        <Typography color="success" sx={{ mr: 2 }}>
                          {success}
                        </Typography>
                      )}
                      <button
                        className="cancel-button"
                        onClick={() => navigate("/dashboard")}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-changes-button"
                        disabled={!isFormChanged}
                        onClick={handleSubmit}
                      >
                        Save Changes
                      </button>
                    </Grid>
                  </form>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
      <div className="my-2">
        <Footer />
      </div>
    </>
  );
}

export { EditProfile };
